import { EditOutlined } from '@ant-design5/icons';
import { ConfigProvider, Empty, theme as antdTheme } from 'antd5';
import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { type Layouts, Responsive } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useTheme } from 'styled-components';

import { PageHeaderLayout } from 'components/PageHeaderLayout';
import { Button } from 'components/ui';
import { Theme } from 'utils/color/horizonTheme';

import { DashboardGrid, EditModeBanner } from './CustomizableDashboard.style';
import { DashboardToolbar } from './DashboardToolbar';
import { useDashboardLayout } from './useDashboardLayout';
import { WidgetCatalogDrawer } from './WidgetCatalogDrawer';
import { WidgetFrame } from './WidgetFrame';
import { ALL_WIDGET_IDS, WIDGET_REGISTRY } from './widgetRegistry';
import { getWidgetSizePresetId } from './widgetSizePresets';

// A single breakpoint = a fixed 12-column grid at every width, so widgets can
// always be placed next to each other (responsive breakpoints were collapsing
// the grid to fewer columns and forcing everything into one stacked column).
const BREAKPOINTS = { lg: 0 };
const COLS = { lg: 12 };
const ROW_HEIGHT = 80;
const MARGIN: [number, number] = [16, 16];

/**
 * The customizable per-user dashboard. View mode looks like a normal dashboard;
 * Edit mode unlocks drag (via the widget header handle), resize, remove, and the
 * Add-widget catalog. Layout lives in `useDashboardLayout`; persistence and
 * role-derived presets layer on in later phases.
 */
export function CustomizableDashboard() {
  const {
    layout,
    isEditing,
    setIsEditing,
    addWidget,
    removeWidget,
    onLayoutChange,
    onInteractionStart,
    onInteractionStop,
    setWidgetSize,
    updateWidgetConfig,
    resetToPreset,
    saveStatus,
  } = useDashboardLayout();
  const [catalogOpen, setCatalogOpen] = useState(false);

  // Measure the grid container ourselves (react-grid-layout's WidthProvider only
  // re-measures on window resize, so it locked a stale/narrow width — causing all
  // tiles to pile up overlapping in the left strip). This width-only observer
  // tracks the real width through sidebar toggles, async content, and window
  // changes — and, crucially, re-renders ONLY when the integer width changes, not
  // when the grid's HEIGHT grows/shrinks as tiles are dragged or resized (which
  // previously fired a re-render on every gesture tick).
  const [gridWidth, setGridWidth] = useState(0);
  const gridObserverRef = useRef<ResizeObserver | null>(null);
  const gridRef = useCallback((node: HTMLDivElement | null) => {
    gridObserverRef.current?.disconnect();
    if (!node) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      setGridWidth(prev => (prev === width ? prev : width));
    });
    observer.observe(node);
    gridObserverRef.current = observer;
  }, []);

  // The app does not dark-theme antd via algorithm, so we scope a ConfigProvider
  // to this feature's antd components (toolbar, drawer, etc.) keyed on the active
  // HorizonTheme, keeping them correct in both light and dark mode.
  const horizonTheme = useTheme();
  const isDark = horizonTheme.themeName === Theme.Dark;

  const isEmpty = layout.instances.length === 0;

  // Widgets are unique on the board, so the catalog only offers ones not yet placed.
  const placedWidgetIds = useMemo(
    () => new Set(layout.instances.map(instance => instance.widgetId)),
    [layout.instances]
  );
  const addableWidgetCount = ALL_WIDGET_IDS.length - placedWidgetIds.size;

  // Memoize each widget's body element keyed on the instance list. Drag/resize only
  // mutate layout geometry (not `instances`), so these element references stay
  // stable across those changes — React then skips re-rendering the heavy widget
  // subtrees (charts, fleet map) on every layout event, which is the bulk of the
  // edit-mode lag with many tiles placed.
  const widgetBodies = useMemo(() => {
    const bodies: Record<string, ReactNode> = {};
    for (const instance of layout.instances) {
      const definition = WIDGET_REGISTRY[instance.widgetId];
      if (!definition) {
        continue;
      }
      const WidgetComponent = definition.component;
      bodies[instance.instanceId] = <WidgetComponent config={instance.config} />;
    }
    return bodies;
  }, [layout.instances]);

  return (
    <PageHeaderLayout title="Dashboard">
      <ConfigProvider
        theme={{
          algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
          token: { colorPrimary: horizonTheme.teal },
        }}
      >
        <DashboardToolbar
          isEditing={isEditing}
          saveStatus={saveStatus}
          addableWidgetCount={addableWidgetCount}
          onToggleEdit={setIsEditing}
          onAddClick={() => setCatalogOpen(true)}
          onReset={resetToPreset}
        />

        {isEditing && (
          <EditModeBanner role="note" data-testid="dashboard-edit-mode-banner">
            <EditOutlined className="edit-mode-banner-icon" />
            <span>
              <strong>Customize mode.</strong> Drag a tile&rsquo;s header to move it, drag its
              corner to resize, and use Add widget or Reset. Changes save automatically.
            </span>
          </EditModeBanner>
        )}

        {isEmpty ? (
          <Empty description="Your dashboard is empty.">
            <Button
              type="primary"
              onClick={() => {
                setIsEditing(true);
                setCatalogOpen(true);
              }}
            >
              Add a widget
            </Button>
          </Empty>
        ) : (
          <DashboardGrid ref={gridRef}>
            {gridWidth > 0 && (
              <Responsive
                className="layout"
                width={gridWidth}
                layouts={layout.layouts as Layouts}
                breakpoints={BREAKPOINTS}
                cols={COLS}
                rowHeight={ROW_HEIGHT}
                margin={MARGIN}
                isDraggable={isEditing}
                isResizable={isEditing}
                draggableHandle=".widget-drag-handle"
                draggableCancel=".widget-no-drag"
                onLayoutChange={onLayoutChange}
                onDragStart={onInteractionStart}
                onResizeStart={onInteractionStart}
                onDragStop={onInteractionStop}
                onResizeStop={onInteractionStop}
                compactType={null}
                preventCollision={true}
              >
                {layout.instances.map(instance => {
                  const definition = WIDGET_REGISTRY[instance.widgetId];
                  if (!definition) {
                    return null;
                  }
                  const title = instance.config?.titleOverride ?? definition.title;
                  const layoutItem = layout.layouts.lg?.find(
                    item => item.i === instance.instanceId
                  );
                  return (
                    <div key={instance.instanceId}>
                      <WidgetFrame
                        instanceId={instance.instanceId}
                        widgetId={instance.widgetId}
                        title={title}
                        dataState={definition.dataState}
                        isEditing={isEditing}
                        selfChrome={definition.selfChrome ?? false}
                        currentSize={getWidgetSizePresetId(layoutItem)}
                        configOptions={definition.configOptions}
                        config={instance.config}
                        onRemove={() => removeWidget(instance.instanceId)}
                        onSizeChange={sizeId => setWidgetSize(instance.instanceId, sizeId)}
                        onConfigChange={cfg => updateWidgetConfig(instance.instanceId, cfg)}
                      >
                        {widgetBodies[instance.instanceId]}
                      </WidgetFrame>
                    </div>
                  );
                })}
              </Responsive>
            )}
          </DashboardGrid>
        )}

        <WidgetCatalogDrawer
          open={catalogOpen}
          placedWidgetIds={placedWidgetIds}
          onClose={() => setCatalogOpen(false)}
          // Keep the drawer open on add/remove so several widgets can be managed at once.
          onAdd={addWidget}
          onRemove={widgetId => {
            const instance = layout.instances.find(inst => inst.widgetId === widgetId);
            if (instance) {
              removeWidget(instance.instanceId);
            }
          }}
        />
      </ConfigProvider>
    </PageHeaderLayout>
  );
}
